import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { CartProvider } from "@/contexts/CartContext";
import { WishlistProvider } from "@/contexts/WishlistContext";
import { ShopifyWishlistProvider } from "@/contexts/ShopifyWishlistContext";
import { LoyaltyProvider } from "@/contexts/LoyaltyContext";
import { PlatformStatusProvider, usePlatformStatus } from "@/contexts/PlatformStatusContext";
import { AlertTriangle } from "lucide-react";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AdminRoute } from "@/components/AdminRoute";
import { MaintenanceGuard } from "@/components/MaintenanceGuard";
import { ComingSoonGuard } from "@/components/ComingSoonGuard";
import WaitingList from "./pages/WaitingList";
import { useCartSync } from "@/hooks/useCartSync";
import { GhostLauncher } from "@/components/GhostChat/GhostLauncher";
import { IntroGate } from "@/components/IntroGate";
import { lazy, Suspense } from "react";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Shop from "./pages/Shop";
import Apparel from "./pages/Apparel";
import { routeLoaders } from "@/lib/routePrefetch";

// Lazy-loaded routes for code splitting. Loaders are shared with the
// prefetch helper so hovering a Link starts the chunk download early.
const ResetPassword = lazy(() => routeLoaders['/reset-password']() as Promise<{ default: React.ComponentType }>);
const Dashboard = lazy(() => routeLoaders['/dashboard']() as Promise<{ default: React.ComponentType }>);
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const ShopifyProductDetail = lazy(() => import("./pages/ShopifyProductDetail"));
const Checkout = lazy(() => routeLoaders['/checkout']() as Promise<{ default: React.ComponentType }>);
const Events = lazy(() => routeLoaders['/events']() as Promise<{ default: React.ComponentType }>);
const EventDetail = lazy(() => import("./pages/EventDetail"));
const EventAlbums = lazy(() => routeLoaders['/event-albums']() as Promise<{ default: React.ComponentType }>);
const Blog = lazy(() => routeLoaders['/blog']() as Promise<{ default: React.ComponentType }>);
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const Wishlist = lazy(() => routeLoaders['/wishlist']() as Promise<{ default: React.ComponentType }>);
const Rewards = lazy(() => routeLoaders['/rewards']() as Promise<{ default: React.ComponentType }>);
const Account = lazy(() => routeLoaders['/account']() as Promise<{ default: React.ComponentType }>);
const Ambassador = lazy(() => routeLoaders['/ambassador']() as Promise<{ default: React.ComponentType }>);
const Admin = lazy(() => routeLoaders['/admin']() as Promise<{ default: React.ComponentType }>);
const AdminProducts = lazy(() => routeLoaders['/admin/products']() as Promise<{ default: React.ComponentType }>);
const AdminCoupons = lazy(() => routeLoaders['/admin/coupons']() as Promise<{ default: React.ComponentType }>);
const AdminAmbassadors = lazy(() => routeLoaders['/admin/ambassadors']() as Promise<{ default: React.ComponentType }>);
const AdminSubAdmins = lazy(() => routeLoaders['/admin/sub-admins']() as Promise<{ default: React.ComponentType }>);
const MyReferrals = lazy(() => routeLoaders['/my-referrals']() as Promise<{ default: React.ComponentType }>);
const NotFound = lazy(() => import("./pages/NotFound"));
const LegalPolicies = lazy(() => routeLoaders['/policies/legal']() as Promise<{ default: React.ComponentType }>);
const NoRefundPolicy = lazy(() => routeLoaders['/policies/no-refund']() as Promise<{ default: React.ComponentType }>);
const LicensePlateDisclaimer = lazy(() => routeLoaders['/policies/license-plate-disclaimer']() as Promise<{ default: React.ComponentType }>);
const DeliveryPolicy = lazy(() => routeLoaders['/policies/delivery']() as Promise<{ default: React.ComponentType }>);
const PrivacyPolicy = lazy(() => routeLoaders['/policies/privacy']() as Promise<{ default: React.ComponentType }>);
const TermsOfService = lazy(() => routeLoaders['/policies/terms']() as Promise<{ default: React.ComponentType }>);
const DomainCommunicationPolicy = lazy(() => routeLoaders['/policies/domain-communication']() as Promise<{ default: React.ComponentType }>);
const NewsletterUnsubscribe = lazy(() => import("./pages/NewsletterUnsubscribe"));
const FAQ = lazy(() => routeLoaders['/faq']() as Promise<{ default: React.ComponentType }>);
const SizeGuide = lazy(() => routeLoaders['/size-guide']() as Promise<{ default: React.ComponentType }>);
const About = lazy(() => routeLoaders['/about']() as Promise<{ default: React.ComponentType }>);
const PartnersSponsors = lazy(() => routeLoaders['/partners']() as Promise<{ default: React.ComponentType }>);
const DriveHUD = lazy(() => import("./pages/drive/DriveHUD"));
const Garage = lazy(() => import("./pages/drive/Garage"));
const MyDrives = lazy(() => import("./pages/drive/MyDrives"));
const DriveLeaderboards = lazy(() => import("./pages/drive/Leaderboards"));
const DriveSummary = lazy(() => import("./pages/drive/DriveSummary"));
const DrivePerformance = lazy(() => import("./pages/drive/Performance"));



const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000, // 1 min: revisits feel instant
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function MaintenanceBanner() {
  const { maintenanceMode } = usePlatformStatus();
  if (!maintenanceMode) return null;
  return (
    <div className="w-full bg-amber-500 text-black px-4 py-2 flex items-center justify-center gap-2 text-sm font-medium z-[100]">
      <AlertTriangle className="w-4 h-4 flex-shrink-0" />
      <span>We're currently undergoing maintenance. Some features may be unavailable.</span>
    </div>
  );
}

function AppContent() {
  useCartSync();
  
  return (
    <>
    <IntroGate>
    <MaintenanceBanner />
    <Suspense fallback={<div className="min-h-screen bg-background" />}>
    <Routes>
      {/* Auth routes — always accessible */}
      <Route path="/auth" element={<Auth />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribe />} />

      {/* Policy pages — always accessible */}
      <Route path="/policies/legal" element={<LegalPolicies />} />
      <Route path="/policies/no-refund" element={<NoRefundPolicy />} />
      <Route path="/policies/license-plate-disclaimer" element={<LicensePlateDisclaimer />} />
      <Route path="/policies/delivery" element={<DeliveryPolicy />} />
      <Route path="/policies/privacy" element={<PrivacyPolicy />} />
      <Route path="/policies/terms" element={<TermsOfService />} />
      <Route path="/policies/domain-communication" element={<DomainCommunicationPolicy />} />

      {/* Public waiting list page */}
      <Route path="/waiting-list" element={<WaitingList />} />

      {/* All other routes — wrapped in ComingSoonGuard so non-admins see the
          waiting-list page when coming_soon_mode is on. Admin routes still
          show the takeover for non-admins (ProtectedRoute kicks in for admins). */}
      <Route path="/" element={<ComingSoonGuard><Index /></ComingSoonGuard>} />
      <Route path="/shop" element={<ComingSoonGuard><MaintenanceGuard><Shop /></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/apparel" element={<ComingSoonGuard><Navigate to="/shop?filter=apparel" replace /></ComingSoonGuard>} />
      <Route path="/dashboard" element={<ComingSoonGuard><MaintenanceGuard><ProtectedRoute><Dashboard /></ProtectedRoute></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/product/:handle" element={<ComingSoonGuard><MaintenanceGuard><ShopifyProductDetail /></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/shop/:slug" element={<ComingSoonGuard><MaintenanceGuard><ProtectedRoute><ProductDetail /></ProtectedRoute></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/checkout" element={<ComingSoonGuard><MaintenanceGuard><ProtectedRoute><Checkout /></ProtectedRoute></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/events" element={<ComingSoonGuard><MaintenanceGuard><ProtectedRoute><Events /></ProtectedRoute></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/events/:slug" element={<ComingSoonGuard><MaintenanceGuard><ProtectedRoute><EventDetail /></ProtectedRoute></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/event-albums" element={<ComingSoonGuard><MaintenanceGuard><ProtectedRoute><EventAlbums /></ProtectedRoute></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/blog" element={<ComingSoonGuard><MaintenanceGuard><ProtectedRoute><Blog /></ProtectedRoute></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/blog/:slug" element={<ComingSoonGuard><MaintenanceGuard><ProtectedRoute><BlogDetail /></ProtectedRoute></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/wishlist" element={<ComingSoonGuard><MaintenanceGuard><ProtectedRoute><Wishlist /></ProtectedRoute></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/rewards" element={<ComingSoonGuard><MaintenanceGuard><ProtectedRoute><Rewards /></ProtectedRoute></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/account" element={<ComingSoonGuard><MaintenanceGuard><ProtectedRoute><Account /></ProtectedRoute></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/account/orders" element={<ComingSoonGuard><MaintenanceGuard><ProtectedRoute><Account /></ProtectedRoute></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/ambassador" element={<ComingSoonGuard><MaintenanceGuard><ProtectedRoute><Ambassador /></ProtectedRoute></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/admin" element={<ComingSoonGuard><MaintenanceGuard><AdminRoute><Admin /></AdminRoute></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/admin/products" element={<ComingSoonGuard><MaintenanceGuard><AdminRoute><AdminProducts /></AdminRoute></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/admin/coupons" element={<ComingSoonGuard><MaintenanceGuard><AdminRoute><AdminCoupons /></AdminRoute></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/admin/ambassadors" element={<ComingSoonGuard><MaintenanceGuard><AdminRoute><AdminAmbassadors /></AdminRoute></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/admin/sub-admins" element={<ComingSoonGuard><MaintenanceGuard><AdminRoute><AdminSubAdmins /></AdminRoute></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/my-referrals" element={<ComingSoonGuard><MaintenanceGuard><ProtectedRoute><MyReferrals /></ProtectedRoute></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/faq" element={<ComingSoonGuard><MaintenanceGuard><FAQ /></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/size-guide" element={<ComingSoonGuard><MaintenanceGuard><SizeGuide /></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/about" element={<ComingSoonGuard><MaintenanceGuard><About /></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/partners" element={<ComingSoonGuard><MaintenanceGuard><PartnersSponsors /></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/drive" element={<ComingSoonGuard><MaintenanceGuard><ProtectedRoute><DriveHUD /></ProtectedRoute></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/drive/garage" element={<ComingSoonGuard><MaintenanceGuard><ProtectedRoute><Garage /></ProtectedRoute></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/drive/drives" element={<ComingSoonGuard><MaintenanceGuard><ProtectedRoute><MyDrives /></ProtectedRoute></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/drive/drives/:id" element={<ComingSoonGuard><MaintenanceGuard><ProtectedRoute><DriveSummary /></ProtectedRoute></MaintenanceGuard></ComingSoonGuard>} />

      <Route path="/drive/performance" element={<ComingSoonGuard><MaintenanceGuard><ProtectedRoute><DrivePerformance /></ProtectedRoute></MaintenanceGuard></ComingSoonGuard>} />
      <Route path="/drive/leaderboards" element={<ComingSoonGuard><MaintenanceGuard><ProtectedRoute><DriveLeaderboards /></ProtectedRoute></MaintenanceGuard></ComingSoonGuard>} />

      <Route path="*" element={<ComingSoonGuard><MaintenanceGuard><NotFound /></MaintenanceGuard></ComingSoonGuard>} />
    </Routes>
    </Suspense>
    <GhostLauncher />
    </IntroGate>
    </>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <PlatformStatusProvider>
        <CartProvider>
          <WishlistProvider>
            <ShopifyWishlistProvider>
              <LoyaltyProvider>
                <TooltipProvider>
                  <Toaster />
                  <Sonner />
                  <BrowserRouter>
                    <AppContent />
                  </BrowserRouter>
                </TooltipProvider>
              </LoyaltyProvider>
            </ShopifyWishlistProvider>
          </WishlistProvider>
        </CartProvider>
      </PlatformStatusProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
